import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { makeStyles, useTheme } from '@mui/styles';
// import { alpha } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Backdrop from '@mui/material/Backdrop';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';

import DeleteIcon from '@mui/icons-material/Delete';
import PublicIcon from '@mui/icons-material/Public';
import ListIcon from '@mui/icons-material/List';
import RestoreIcon from '@mui/icons-material/Restore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TransformIcon from '@mui/icons-material/Transform';

import { format as D3format } from 'd3';
import Moment from 'moment';
import {
    DataGridPro,
    LicenseInfo,
    useGridApiRef as useApiRef,
} from '@mui/x-data-grid-pro';
import {
    get as _get,
    set as _set,
} from 'lodash';
import { arrayMoveImmutable as arrayMove } from 'array-move';

import BubbleTabs from '../../../components/bubbletabs';
import { EMPTY_FILTER } from '../../../components/filter/filterbuilder';
import TagList from '../../../components/tags/taglist';
import IncidentResponseList from '../../../components/incident-response-list';

import BarChart from '../../../components/charts/barchart';
import DonutChart from '../../../components/charts/donutchart';
import GeoMap from '../../../components/charts/geomap';
import PopupMenu from '../../../components/popupmenu';
import IPAddress from '../../../components/ipaddress';
import FileHash from '../../../components/filehash';
import SensorName from '../../../components/sensorname';
import { BTJob } from '../../../components/bt-jobs';
import Tag from '../../../components/tags/tag';
import Mitre, { MitreList } from '../../../components/mitre';
import CountBar from '../../../components/charts/countbar';
import Sparkline from '../../../components/charts/sparkline';
import Tooltip from '../../../components/styled/tooltip';

import _AlertsService, { DefaultWidths } from '../../../data/alerts-service';
import DataContext from '../../../data/data-context';
import PolicyService from '../../../data/policy-service';
import IncidentService from '../../../data/incident-service';
import Auth, { User, ProfileContext, ParamsSerializer } from '../../../data/auth-service';
import { GroupsContext, Severities } from '../../../data/groups-service';
import ReportsService from '../../../data/reports-service';
import CMCsDataService, { CmcContext } from '../../../data/cmcs-service';
import { skyBlue, neutrals2 } from '../../../data/colors';

import { GetISODateFromQuick, CopyToClipboard, SaveFile, ToArray, GetBTUUID } from '../../../libraries/helpers';
import usePrevious from '../../../libraries/useprevious';
import useInterval from '../../../libraries/useinterval';
// import { useWhatChangedEffect } from '../../../libraries/withwhyrender';
import useWindowSize from '../../../libraries/usewindowsize';
import { MergeNodes } from '../../../d3/utils';

import { LoadingOverlay, NoCMCLoadingOverlay, NoRowsOverlay } from '../../../components/xgrid/overlays';
import AlertDetail from './alert-detail';
import TableToolbar, {
    ListMode,
    DetailMode,
    HistoryMode,
    FivetupleMode,
    ThreetupleMode,
    MapMode,
} from '../components/table-toolbar';
import GeomapToolbar from '../components/geomap-toolbar';
import TableFooter from '../components/footer';
import ReportsToast from '../components/reports-toast';
import ColumnSelector from './column-selector';
import FilterBar, {
    QUICK_HOUR,
    // QUICK_DAY,
    // QUICK_WEEK,
    QUICK_JOB,
    BTMODE_BT,
    BTMODE_LIVE,
    BTMODE_NONE,
    GetRangeFromJobs,
    GetCreatedFromJobs,
    GetBacktestingEnglish,
} from '../components/filter-bar';
import ConfirmationDialog from '../../../components/confirmationdlg';

LicenseInfo.setLicenseKey(
    'ee66e48134e22572dc0e58e58a9bec79Tz00NDc4MSxFPTE2ODU1NDUwNDMzNTAsUz1wcm8sTE09c3Vic2NyaXB0aW9uLEtWPTI=',
);

const useStyles = makeStyles(theme => ({
    content: {
        flexGrow: 1,
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        marginRight: -10,
        position: 'relative',
    },
    table: {
        '& .backtesting': {
            backgroundColor: theme.palette.backtesting.table,
        },
        '&.list .read': {
            opacity: 0.6,
            // fontWeight: 'normal',
            // backgroundColor: alpha(theme.palette.background.default, 0.6),
            //
            // '&.backtesting': {
            //     backgroundColor: alpha(theme.palette.backtesting.table, 0.6),
            // },
            // backgroundColor: theme.palette.backtesting.table,

            // '&.backtesting': {
            //     opacity: 1,
            //     backgroundColor: alpha(theme.palette.backtesting.read, 0.6),
            // },
        },
        '& .MuiDataGrid-main': {
            zIndex: theme.zIndex.drawer - 1,
            backgroundColor: theme.palette.background.default,
            // fontWeight: 'bold',
        },
    },
    backdrop: {
        zIndex: theme.zIndex.drawer - 2,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
    },
    compressed: {
        backgroundColor: skyBlue[350],
        color: 'white',
        borderRadius: 5,
        fontSize: 'small',
        width: 25,
        height: 25,
        lineHeight: '25px',
        textAlign: 'center'
    },
    mapview: {
        border: `1px solid ${neutrals2[600]}`,
        borderRadius: 4,
        marginTop: 8,
    },
    stealRoom: {
        paddingLeft: 13,
        paddingRight: 13,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
    },
    englishGreen: {
        color: theme.palette.tertiary.main,
    },
    englishtooltip: {
        maxWidth: 500,
    },
    english: {
        position: 'relative',
        '& div': {
            display: 'block',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            fontSize: 'small',
            color: theme.palette.tertiary.main,
            position: 'absolute',
            top: 5,
            left: 10,
            width: '95%',
        }
    },
    nowrap: {
        // display: 'block',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
    },
    mapped: {
        verticalAlign: 'middle',
        paddingRight: 5,
    },
}));

const modes = [
    { key: ListMode, icon: <ListIcon/>, title: 'list alerts' },
    { key: DetailMode, icon: <VisibilityIcon/>, title: 'view details' },
    { key: HistoryMode, icon: <RestoreIcon/>, title: 'recently viewed' },
    { key: FivetupleMode, icon: '5', title: 'group by 5-tuple' },
    { key: ThreetupleMode, icon: '3', title: 'group by client-server ID' },
    { key: MapMode, icon: <PublicIcon/>, title: 'show map' },
];

const MC2CHECK = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';
const ALERTS_LIMIT = 500;
const REFRESH_RATE = 11000;
const TIMELINE_HEIGHT = MC2CHECK ? 10 : 110;
const PAGE_HEADER = 130;

const Alerts = ({ profile = 'alerts', ...props }) => {
    const toolbarRef = useRef(null);
    const updatedRef = useRef(null);
    const classes = useStyles();
    const theme = useTheme();
    const tableAPIRef = useApiRef();
    const user = User().username;
    const navigate = useNavigate();
    const location = useLocation();
    const toastId = useRef(null);
    const { width, height } = useWindowSize();

    const { updateProfile, getProfile } = useContext(ProfileContext);
    const {
        assets,
        tags,
        sensorNames,
        backtestingJobsLite,
        backtestingJobs,
        clearAlertMitre,
        setAlertMitre,
        clearAlertsMitre,
        setAlertsMitre,
        alertMappings: mappings,
        setAlertMappings: setMappings,
        systemSettings,
    } = useContext(DataContext);

    const perms = Auth.permissions.get('Alerts');
    const gatorPerms = Auth.permissions.get('DataTuningPolicy');
    const incidentPerms = Auth.permissions.get('Incidents');
    const tagPerms = Auth.permissions.get('Tags');
    const cmcPerms = Auth.permissions.get('CMC');

    // data state
    const [data, setData] = useState([]);
    const [tagging, setTagging] = useState(false);
    const [quick, setQuick] = useState(QUICK_HOUR);
    const [sort, setSort] = useState('timestamp desc');
    const [search, setSearch] = useState();
    const [range, setRange] = useState([undefined, undefined]);
    const [filter, setFilter] = useState();
    const [groups, setGroups] = useState([]);
    const [tagFilters, setTagFilters] = useState([]);
    const [tagsOp, setTagsOp] = useState();
    const [read, setRead] = useState('all');
    const [backtesting, setBacktesting] = useState(BTMODE_NONE);
    const [selectedBTJobs, setSelectedBTJobs] = useState([]);
    const [savedFilters, setSavedFilters] = useState([]);

    // page state
    const [init, setInit] = useState();
    // mode is a stack
    const [mode, setMode] = useState([ListMode]);
    const [currentAlert, setCurrentAlert] = useState();
    const [prevAlert, setPrevAlert] = useState();
    const [nextAlert, setNextAlert] = useState();
    const [showTags, setShowTags] = useState(false);
    const [showMitre, setShowMitre] = useState(false);
    const [haveIncidents, setHaveIncidents] = useState(false);
    const [showIncidents, setShowIncidents] = useState(false);
    const [showColumnsSel, setShowColumnsSel] = useState(false);
    const [showFilter, setShowFilter] = useState(false);
    const [showFilterTimeout, setShowFilterTimeout] = useState();
    const [fields, setFields] = useState([]);
    const [tableHeight, setTableHeight] = useState(800);
    const [selection, setSelection] = useState();
    const [grouping, setGrouping] = useState(false);
    const [groupFilter, setGroupFilter] = useState();
    const [columns, setColumns] = useState([]);
    const [visibleColumns, setVisibleColumns] = useState({});
    const [widths, setWidths] = useState(DefaultWidths);
    const [hideColumns, setHideColumns] = useState({});
    const [english, setEnglish] = useState('');
    const [savedFilter, setSavedFilter] = useState('');
    const [hideFilterbar, setHideFilterbar] = useState(true);
    const [hideTimelinebar, setHideTimelinebar] = useState(true);
    const [interval, setInterval] = useState(true);
    const [canTune, setCanTune] = useState(false);
    const [wantPolicy, setWantPolicy] = useState();

    const [pause, setPause] = useState(false);
    const [refreshRate, setRefreshRate] = useState(REFRESH_RATE);
    const [refresh, setRefresh] = useState();
    const [confirmDlg, setConfirmDlg] = useState(false);

    // paging state
    const [total, setTotal] = useState(0);
    const [asof, setAsof] = useState();
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    // charts state
    const [timeline, setTimeline] = useState([]);
    const [tlselection, setTLSelection] = useState();
    const [donutFilter, setDonutFilter] = useState();
    const [geodata, setGeodata] = useState({});
    const [geoHeartbeat, setGeoHeartbeat] = useState(0);
    const [groupedData, setGroupedData] = useState([]);
    const [donutData, setDonutData] = useState([]);
    const [groupKeys, setGroupKeys] = useState([]);
    const [maxGroup, setMaxGroup] = useState(0);
    const [AlertsService] = useState(props.service || _AlertsService);

    // mc2 cmc state - cmc context is set at the app level
    // cmc is an object { uuid, name } - stored in page profile as uuid
    const { cmc, setCmc } = useContext(CmcContext);
    // cmcs is an object { uuid: name, uuid: name }
    const [cmcs, setCmcs] = useState();
    // selectedCmcs is a string array [uuid, uuid, uuid]
    const [selectedCmcs, setSelectedCmcs] = useState();

    // Track tab visibility
    const [tabVisible, setTabVisible] = useState(true);
    
    // Page Visibility API effect
    useEffect(() => {
        const handleVisibilityChange = () => {
            setTabVisible(!document.hidden);
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        setTabVisible(!document.hidden);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);


    // rested handles this switch over
    const defaultSort = useMemo(() => {
        return 'timestamp desc';
    }, []);

    const jobCreated = useMemo(() => {
        return GetCreatedFromJobs(selectedBTJobs, backtestingJobsLite);
    }, [selectedBTJobs, backtestingJobsLite]);

    const untag = useCallback((d, tag) => {
        if (groups.length > 0) {
            setConfirmDlg({
                msg: "This may be a long operation. You will be notified when it's complete",
                callback: () => {
                    setConfirmDlg(false);
                    let f = AlertsService.filterFromGroups(d, groups);
                    if (filter && (filter.nodes || []).length > 0) {
                        f = { operator: 'And', nodes: [filter, f] };
                    }

                    // async call
                    AlertsService.untagAlerts(tag, {
                        search,
                        start: range[0],
                        end: range[1],
                        filter: f,
                        tagFilters,
                        tagsOp,
                        read,
                        backtesting,
                        selectedBTJobs,
                        jobCreated,
                        cmc,
                    }).catch(handleError);
                }
            });
        } else {
            AlertsService.untagAlert(tag, d, cmc);
        }
        // remove immediately from selected row
        const i = _get(d, 'data.bricata.tag', []).findIndex(t => t === tag);
        if (i >= 0) {
            d.data.bricata.tag.splice(i, 1);
            tableAPIRef.current.updateRows([{ ...d }]);
        }
    }, [
        search,
        filter,
        tagFilters,
        tagsOp,
        read,
        backtesting,
        selectedBTJobs,
        jobCreated,
        range,
        tableAPIRef,
        groups,
        cmc,
        AlertsService,
    ]);

    const unmitre = useCallback((d, tag) => {
        clearAlertMitre(d.uuid, cmc);

        // remove immediately from selected row
        d.data.bricata.mitre_category = '';
        tableAPIRef.current.updateRows([{ ...d }]);
    }, [clearAlertMitre, tableAPIRef, cmc]);

    const mitre = useCallback((d, mitre) => {
        setAlertMitre(d.uuid, mitre, cmc);

        // apply immediately to selected row
        d.data.bricata.mitre_category = mitre;
        tableAPIRef.current.updateRows([{ ...d }]);
    }, [setAlertMitre, tableAPIRef, cmc]);

    const getMappedValues = useCallback((m, row) => {
        return m.map(f => AlertsService.columnsMap[f])
            .reduce((a, c) => [
                ...a,
                ...ToArray(AlertsService.value(row, c))
                    .map(f => ({ title: c.title, value: (f || '').trim() })),
            ], []);
    }, [AlertsService]);

    const getCellRenderers = useCallback(() => {
        return {
            tag: p => {
                const v = typeof p.row === 'object' ? _get(p.row, 'data.bricata.tag') : p.row;
                if (!v) return '';
                return v.map((t, i) =>
                    <Tag
                        key={t + i}
                        tag={t}
                        onUntag={t => untag(p.row, t)}
                        untagLabel={'untag'}/>
                );
            },
            mitre_category: p => {
                const v = typeof p.row === 'object' ? _get(p.row, 'data.bricata.mitre_category') : p.row;
                if (!v) return '';
                return <Mitre
                    mitre={v}
                    onUnmitre={p.row.count ? undefined : v => unmitre(p.row, v)}
                    onUpdateMitre={p.row.count ? undefined : v => mitre(p.row, v)}/>;
            },
            md5: p => {
                const v = typeof p.row === 'object' ? _get(p.row, 'data.bro.md5') : p.row;
                if (!v) return '';
                return <FileHash hash={v} title='MD5'/>;
            },
            sha1: p => {
                const v = typeof p.row === 'object' ? _get(p.row, 'data.bro.sha1') : p.row;
                if (!v) return '';
                return <FileHash hash={v} title='SHA1'/>;
            },
            sha256: p => {
                const v = typeof p.row === 'object' ? _get(p.row, 'data.bro.sha256') : p.row;
                if (!v) return '';
                return <FileHash hash={v} title='SHA256'/>;
            },
            xff: p => {
                let v = p.row;
                if (typeof p.row === 'object') {
                    v = ToArray(_get(p.row, 'data.http.xff'));
                }
                return v.map((vv, i) => vv
                    ? <span key={`val_${i}`} style={{ paddingLeft: 15 }}>
                        <IPAddress key={i} address={vv} uuid={_get(p.row, 'data.bricata.sensor_uuid')}/>
                    </span> : '');
            },
            xorig: p => {
                let v = p.row;
                if (typeof p.row === 'object') {
                    v = _get(p.row, 'data.http.xorig');
                }
                return <IPAddress address={v} uuid={_get(p.row, 'data.bricata.sensor_uuid')}/>;
            },
            sensor_uuid: p => {
                const v = typeof p.row === 'object' ? _get(p.row, 'data.bricata.sensor_uuid') : p.row;
                return <SensorName uuid={'' + v} />;
            },
            job_uuid: p => {
                const btuuid = GetBTUUID(p.row, 'data.bt_uuid');
                return btuuid ? <BTJob uuid={'' + btuuid} /> : '';
            },
            severity: p => {
                const v = typeof p.row === 'object' ? _get(p.row, 'data.alert.severity') : p.row;
                const color = v <= theme.SeverityColors.length && v > 0
                    ? theme.SeverityColors[v - 1] : '';
                return <Chip
                    style={{
                        backgroundColor: color,
                        color: theme.palette.getContrastText(color),
                    }}
                    size="small"
                    label={ v } />;
            },
            compressed: p => {
                const v = typeof p.row === 'object' ? _get(p.row, 'data.bricata.compressed') : p.row;
                if (!v) return '';
                return <div className={classes.compressed}>{v}</div>;
            },
            count: p => {
                const v = typeof p.row === 'object' ? p.row.count : p.row;
                if (!v) return '';
                const width = Math.max(p.colDef.width - 20, 7);
                return <span style={{ textAlign: 'left', lineHeight: 1.5 }}>
                    {D3format(',')(v)}
                    <div style={{ lineHeight: 0 }}>
                        <CountBar value={+v} colorKey={p.row.key} width={width}/>
                    </div>
                </span>;
            },
            activity: p => {
                if (!p.row || p.row.key === undefined) return;
                return <span style={{ lineHeight: 1.5 }}>
                    <Sparkline
                        dataservice={AlertsService}
                        data={p.row.timeline}
                        uuid={p.row.key}
                        colorKey={p.row.key}/>
                </span>;
            },
            // mapped columns
            src_ip: p => {
                let _v = [p.row];
                let c;
                const m = mappings.src_ip || ['src_ip'];
                if (typeof p.row === 'object') {
                    _v = getMappedValues(m, p.row);
                    c = _get(p.row, 'data.bricata.src_location.country');
                }
                return _v.map((v, i) => {
                    return v.value ? <span key={`val_${i}`} style={{ paddingLeft: 15 }}>
                        <IPAddress
                            key={`val_${i}`}
                            country={c}
                            address={v.value}
                            tipPrefix={`${v.title}: `}
                            uuid={_get(p.row, 'data.bricata.sensor_uuid')}/>
                    </span> : '';
                });
            },
            dest_ip: p => {
                let _v = [p.row];
                let c;
                const m = mappings.dest_ip || ['dest_ip'];
                if (typeof p.row === 'object') {
                    _v = getMappedValues(m, p.row);
                    c = _get(p.row, 'data.bricata.dest_location.country');
                }
                return _v.map((v, i) => {
                    return v.value ? <span key={`val_${i}`} style={{ paddingLeft: 15 }}>
                        <IPAddress
                            key={`val_${i}`}
                            country={c}
                            address={v.value}
                            tipPrefix={`${v.title}: `}
                            uuid={_get(p.row, 'data.bricata.sensor_uuid')}/>
                    </span> : '';
                });
            },
            src_port: p => {
                let _v = [p.row];
                const m = mappings.src_port || ['src_port'];
                if (typeof p.row === 'object') {
                    _v = getMappedValues(m, p.row);
                }
                return _v.map((v, i) => v.value ? <Tooltip
                    key={`val_${i}`}
                    arrow
                    placement="top"
                    title={v.title}>
                    <span style={{ paddingRight: 10 }}>{v.value}</span>
                </Tooltip> : ''
                );
            },
            dest_port: p => {
                let _v = [p.row];
                const m = mappings.dest_port || ['dest_port'];
                if (typeof p.row === 'object') {
                    _v = getMappedValues(m, p.row);
                }
                return _v.map((v, i) => v.value ? <Tooltip
                    key={`val_${i}`}
                    arrow
                    placement="top"
                    title={v.title}>
                    <span style={{ paddingRight: 10 }}>{v.value}</span>
                </Tooltip> : ''
                );
            },
        };
    }, [classes, theme, untag, mitre, unmitre, AlertsService, getMappedValues, mappings]);

    const isMappedColumn = useCallback(v => {
        if (!_get(mappings, `${v}.length`)) return false;
        if (mappings[v].length > 1) return true;
        return (mappings[v][0] !== v);
    }, [mappings]);

    // header rendering for mapped values
    // <Chip className={classes.mapped} label={c.title} color='primary'/>
    const getHeaderRenderers = useCallback(() => {
        const mappedHeader = c => {
            const ms = mappings[c.variable].map(m => `${AlertsService.columnsMap[m].title}`);
            return (<Tooltip
                arrow placement="top"
                size="small"
                title={ms.join(', ')}>
                <span><TransformIcon color='primary' className={classes.mapped} size='small'/>{c.title}</span>
            </Tooltip>);
        };

        return {
            src_ip: p => {
                const c = AlertsService.columnsMap.src_ip;
                return isMappedColumn(c.variable) ? mappedHeader(c) : undefined;
            },
            dest_ip: p => {
                const c = AlertsService.columnsMap.dest_ip;
                return isMappedColumn(c.variable) ? mappedHeader(c) : undefined;
            },
            src_port: p => {
                const c = AlertsService.columnsMap.src_port;
                return isMappedColumn(c.variable) ? mappedHeader(c) : undefined;
            },
            dest_port: p => {
                const c = AlertsService.columnsMap.dest_port;
                return isMappedColumn(c.variable) ? mappedHeader(c) : undefined;
            }
        };
    }, [classes, mappings, isMappedColumn, AlertsService.columnsMap]);

    const getTableTypes = useCallback(() => ({
        sev: { typs: 'number' },
        int: { type: 'number' },
        timestamp: {
            type: 'dateTime',
            valueFormatter: ({ value }) => {
                const d = Moment(value);
                return d.isValid() ? Moment(d).format(theme.date.long) : value;
            }
        },
        date: {
            type: 'dateTime',
            valueFormatter: ({ value }) => {
                const d = Moment(value);
                return d.isValid() ? Moment(d).format(theme.date.long) : value;
            }
        },
    }), [theme]);

    const getCmc = useCallback(uuid => {
        if (!(cmcs || {})[uuid]) return undefined;
        return { uuid, name: cmcs[uuid] };
    }, [cmcs]);

    const defaultColumns = useCallback((columns) => {
        const currentMap = columns.reduce((a, c) => ({ ...a, [c.variable]: c }), {});
        return fields
            .filter(f => !f.tableHidden)
            .map(f => {
                const { order, hide, ...rest } = currentMap[f.variable];
                return {
                    ...f,
                    ...rest,
                    hide: f.addon ? rest.columnSelectorHidden : f.hide
                };
            })
            .sort((a, b) => {
                if (a.hide && !b.hide) return 1;
                if (b.hide && !a.hide) return -1;
                if (a.order === undefined && b.order === undefined) {
                    return a.variable.localeCompare(b.variable);
                }
                return (a.order || 0) - (b.order || 0);
            });
    }, [fields]);

    const resetPage = useCallback(() => {
        const defaultStart = GetISODateFromQuick(QUICK_HOUR); 
        setQuick(QUICK_HOUR);
        setSort('timestamp desc');
        setGroups([]);
        setSearch('');
        setTagsOp('Or');
        setTagFilters([]);
        setFilter(EMPTY_FILTER);
        setRange([defaultStart, undefined]);
        setGrouping(false);
        setRead('all');
        setBacktesting(_get(systemSettings, 'support.backtesting.ui', false)
            ? BTMODE_LIVE : BTMODE_NONE);
        setSelectedBTJobs([]);
        setMode([ListMode]);
        setColumns(current => defaultColumns(current));
    }, [defaultColumns, systemSettings]);

    // backward compatibility settings will be set first time only
    const applyPageState = useCallback((state) => {
        if (!state) return resetPage();

        const user = User().username;

        // need to sanitize the data coming in and convert from old fields to new fields
        if (state.end === '') delete state.end;
        if (state.start === '') delete state.start;

        if (state.startTime) {
            state.start = state.startTime;
        }
        if (state.endTime) {
            state.end = state.endTime;
        }
        if (state._mode) {
            state.mode = state._mode;
        }
        if (state.group) {
            state.groups = state.group;
        }
        if (state._groups) {
            state.groups = state._groups;
        }
        if (state._columns) {
            state.columns = state._columns.reduce((a, c, i) => {
                return { ...a, [c]: { name: c, order: i } };
            }, {});
        }
        if (state._quickFilter && state._quickFilter.length > 0) {
            state.quick = state._quickFilter;
        }
        if (state.sortCol && state.sortOrder) {
            state.sort = `${state.sortCol} ${state.sortOrder}`;
        }
        if (state._dynamicColWidth) {
            state.widths = state._dynamicColWidth;
        }
        if (state.tags) {
            state.tagFilters = state.tags;
        }

        // only some modes are stashable
        const allowable = [ListMode, FivetupleMode, ThreetupleMode, MapMode];
        state.mode = allowable.find(a => a === state.mode) || ListMode;

        // add tuple groups if missing
        if (state.mode === FivetupleMode) {
            state.groups = ['community_id'];
        } else if (state.mode === ThreetupleMode) {
            state.groups = ['clisrv_id'];
        }

        // apply quick filters
        if (state.quick) {
            state.end = undefined;
            state.start = GetISODateFromQuick(state.quick);
        }

        // map groups to group field
        state.groups = (state.groups || []).map(g => {
            if (typeof g === 'object') g = g.key || g.variable;
            return AlertsService.columnsMap[g];
        });

        // columns preferences are stashed in the profile but are not part of saved filters for consistency
        const ww = state.widths || DefaultWidths;
        const hd = state.hideColumns || {};
        const cellRenderers = getCellRenderers();
        const headerRenderers = getHeaderRenderers();
        const tableTypes = getTableTypes();

        // defense against bad sorting
        if (state.mode !== HistoryMode && (state.sort || '').includes('remove')) {
            state.sort = 'timestamp desc';
        }

        // apply column preferences, or leave alone or set to defaults if there are no columns yet
        const cols = fields
            .filter(f => !f.tableHidden)
            .map(f => ({
                ...f,
                sortable: f.indexed || !!f.sortable,
                headerName: f.title,
                valueGetter: p => AlertsService.value(p.row, f),
                type: 'string',
                hide: state.columns ? state.columns[f.variable] === undefined : hd[f.variable] || f.hide,
                columnSelectorHidden: f.variable === 'remove' ||
                    (!!f.grouping && state.groups.length < 1),
                renderCell: cellRenderers[f.variable],
                renderHeader: headerRenderers[f.variable],
                order: (state.columns && state.columns[f.variable])
                    ? state.columns[f.variable].order : f.order,
                width: ww[f.variable] || DefaultWidths[f.variable] || 100,
                cellClassName: p => {
                    const bt = GetBTUUID(p.row, 'data.bt_uuid') ? 'backtesting' : '';
                    const _user = MC2CHECK ? `MC2 User: ${user}` : user;
                    return _get(p, 'row.data.bricata.read', [])
                        .find(r => r === _user) ? `read ${bt}` : bt;
                },

                ...tableTypes[f.type]
            }))
            .sort((a, b) => {
                const av = _get(state.columns, a.variable);
                const bv = _get(state.columns, b.variable);
                if (av && !bv) return -1;
                if (bv && !av) return 1;
                if (a.order === undefined && b.order === undefined) {
                    return a.variable.localeCompare(b.variable);
                }
                return (a.order || 0) - (b.order || 0);
            });

        setColumns(cols);
        setWidths(current => ({ ...DefaultWidths, ...current, ...state.widths }));
        setHideColumns(current => ({ ...current, ...state.hideColumns }));
        // mode is a stack, but state.mode is not!
        setMode([state.mode]);
        setPause(state.pause);
        setSort(state.sort || defaultSort);
        setGroups(state.groups || []);
        setGrouping((state.groups || []).length > 0);
        setQuick(state.quick);
        setSearch(state.search);
        setTagsOp(state.tagsOp || 'or');
        setTagFilters(state.tagFilters || []);
        setFilter(state.filter);
        setRange(state.quick === 'job'
            ? GetRangeFromJobs(state.selectedBTJobs, backtestingJobs)
            : [state.start, state.end]);
        setRead(state.read || 'all');
        // bootstrap condition - don't update if we don't have the support setting yet.
        const btui = _get(systemSettings, 'support.backtesting.ui');
        setBacktesting(btui === undefined ? state.backtesting : !btui ? BTMODE_NONE
            : state.backtesting !== undefined ? state.backtesting
                : BTMODE_LIVE);
        setSelectedBTJobs(state.selectedBTJobs || []);
        setSelectedCmcs(state.selectedCmcs);
        setCmc(getCmc(state.cmc));
    }, [
        fields,
        getCellRenderers,
        getHeaderRenderers,
        getTableTypes,
        AlertsService,
        setCmc,
        getCmc,
        resetPage,
        systemSettings,
        defaultSort,
        backtestingJobs,
    ]);

    const currentMode = useMemo(() => mode.length < 1 ? ListMode : mode[mode.length - 1], [mode]);

    const getCurrentState = useCallback(() => {
        return {
            sort: currentMode === HistoryMode ? defaultSort : sort,
            search,
            start: range[0],
            end: range[1],
            quick,
            filter,
            tagFilters,
            tagsOp,
            groups: groups ? groups.map(g => g.variable) : undefined,
            read,
            backtesting,
            selectedBTJobs,
            mode: currentMode,
            pause,
            columns: visibleColumns,
            selectedCmcs,
            cmc: (cmc || {}).uuid,
        };
    }, [
        sort,
        search,
        range,
        filter,
        tagFilters,
        tagsOp,
        groups,
        read,
        backtesting,
        selectedBTJobs,
        currentMode,
        pause,
        visibleColumns,
        quick,
        selectedCmcs,
        cmc,
        defaultSort,
    ]);

    const safeSort = useCallback((c, o, fallback) => {
        const col = AlertsService.columnsMap[c];

        if (col && !col.hide) {
            return [c, o];
        }

        if (fallback) {
            return fallback;
        }

        let i = 0;
        let next = columns[i];
        while (!next.sortable && i < columns.length) {
            i++;
            next = columns[i];
        }
        return [next.variable, o];
    }, [columns, AlertsService]);

    // sort if the column is visible
    const safeSetSort = useCallback((c, o, fallback) => {
        const ss = safeSort(c, o, fallback);
        setSort(`${ss[0]} ${ss[1]}`);
    }, [safeSort]);

    const handleSelectionChange = (newSelection) => {
        if (tagging) setSelection(newSelection);
    };

    const handleOnCellClick = useCallback((params, e) => {
        if (window.getSelection().type === 'Range') {
            e.preventDefault();
            e.stopPropagation();
        }
    }, []);

    const doReadAlert = useCallback((alert, read) => {
        AlertsService.doReadAlert(alert.uuid, read, cmc);
        const _user = MC2CHECK ? `MC2 User: ${user}` : user;

        if (read) {
            if (!_get(alert, 'data.bricata.read')) _set(alert, 'data.bricata.read', []);
            if (!alert.data.bricata.read.find(u => u === _user)) {
                alert.data.bricata.read.unshift(_user);
            }
        } else {
            const i = _get(alert, 'data.bricata.read', []).findIndex(u => u === _user);
            if (i >= 0) {
                alert.data.bricata.read.splice(i, 1);
            }
        }

        // update row immediately
        tableAPIRef.current.updateRows([{ ...alert }]);
    }, [tableAPIRef, AlertsService, cmc, user]);

    const navigateToAlert = useCallback(alert => {
        if (data.length < 1) return;
        let i = 0;
        if (!alert) {
            alert = data[0];
        } else {
            i = data.findIndex(a => a.uuid === alert.uuid);
        }

        const prev = i > 0 ? data[i - 1] : undefined;
        const next = i >= 0 && i < data.length ? data[i + 1] : undefined;
        setCurrentAlert({ ...alert, rowIndex: i });
        setPrevAlert(prev);
        setNextAlert(next);

        setMode(current => {
            if (current.length > 0 && current[current.length - 1] === DetailMode) {
                // update in place to avoid re-render
                current[current.length - 1] = DetailMode;
                return current;
            }
            return [...current, DetailMode];
        });

        // add to history - don't add dup but bump to the top of the list
        const { quick, columns, read, ...state } = getCurrentState();
        const profileData = getProfile(profile);
        const history = (profileData.lastViewed || []);
        i = history.findIndex(h => h.uuid === alert.uuid);
        if (i >= 0) history.splice(i, 1);
        history.unshift({ timestamp: alert.timestamp, uuid: alert.uuid, state });
        history.splice(42);
        updateProfile(profile, { ...profileData, lastViewed: history });

        // mark alert as read
        doReadAlert(alert, true);
    }, [data, updateProfile, doReadAlert, profile, getProfile, getCurrentState]);

    const handleModeChange = useCallback(v => {
        if (showTags || showIncidents || showMitre) return;
        AlertsService.cancelAll();
        setMode(current => {
            // go to the first guy in the page
            if (v === DetailMode) {
                navigateToAlert();
                return [...current, v];
            }

            const lastMode = current.length < 1 ? ListMode : current[current.length - 1];
            if (v === FivetupleMode) {
                setGroups([AlertsService.columnsMap.community_id]);
                setGrouping(true);
            } else if (v === ThreetupleMode) {
                setGroups([AlertsService.columnsMap.clisrv_id]);
                setGrouping(true);
            } else if (v === ListMode) {
                if (lastMode === ThreetupleMode || lastMode === FivetupleMode) {
                    setGroups(gCurrent => (gCurrent && gCurrent.length > 0) ? [] : gCurrent);
                    setGrouping(false);
                }
            }
            return [...current, v];
        });
    }, [
        AlertsService,
        navigateToAlert,
        showTags,
        showIncidents,
        showMitre,
    ]);

    const navigateToGroup = useCallback((alert) => {
        const groupFilter = AlertsService.filterFromGroups(alert, groups);
        (groupFilter.nodes || []).map(f => ({ ...f, expanded: true }));
        groupFilter.expanded = true;

        // don't want to double kick the table
        setFilter(current => {
            if (current && (current.nodes || []).length > 0) {
                if (current.operator === 'And') {
                    groupFilter.nodes.push(...current.nodes);
                } else {
                    groupFilter.nodes.push(current);
                }
            }
            return groupFilter;
        });
        setGroups(current => (current && current.length > 0) ? [] : current);
        setGrouping(false);
        setMode(current => [...current, ListMode]);
        setShowFilterTimeout({ exit: 4200 });

        const timeoutId = setTimeout(() => {
            setShowFilterTimeout();
        }, 4200);
        return () => clearTimeout(timeoutId);
    }, [AlertsService, groups]);

    const handleViewAlert = useCallback((rowData, prev, next) => {
        if (tagging || showTags || showIncidents || showMitre) return;
        if (_get(rowData, 'row.count', 0) > 0) navigateToGroup(rowData.row);
        else navigateToAlert(rowData.row);
    }, [tagging, showTags, showIncidents, showMitre, navigateToGroup, navigateToAlert]);

    const handleOnQuickFilter = useCallback(q => {
        setQuick(q.key);
        const daRange = q.key === 'job'
            ? GetRangeFromJobs(selectedBTJobs, backtestingJobs)
            : [GetISODateFromQuick(q.key), undefined];
        setRange(daRange);
    }, [selectedBTJobs, backtestingJobs]);

    const handleSelectBTJobs = useCallback(jobs => {
        setRange(current => quick === QUICK_JOB
            ? GetRangeFromJobs(jobs, backtestingJobs) : current);
        setSelectedBTJobs(jobs);
    }, [quick, backtestingJobs]);

    const handleSetBacktesting = useCallback((v, pq, pj, pr) => {
        setBacktesting(v);
        setQuick(v === BTMODE_BT ? QUICK_JOB : pq);
        setSelectedBTJobs(v === BTMODE_BT ? pj : []);
        setRange(current => v === BTMODE_BT
            ? GetRangeFromJobs(pj, backtestingJobs)
            : pq ? [GetISODateFromQuick(pq), undefined]
                : pr);
        // if in detail view, go back to list view
        setMode(current => {
            const lastMode = current.length < 1 ? ListMode : current[current.length - 1];
            return lastMode === DetailMode ? [...current, ListMode] : current;
        });
    }, [backtestingJobs]);

    const handleBacktestingJobsUpdated = useCallback(() => {
        setRange(current => {
            if (quick === QUICK_JOB) {
                // noise reduction as much as possible
                const btJobsRange = GetRangeFromJobs(selectedBTJobs, backtestingJobs);
                if ((current || []).length < 2) return btJobsRange;
                current[0] = btJobsRange[0];
                current[1] = btJobsRange[1];
                return current;
            }
            return current;
        });
    }, [quick, selectedBTJobs, backtestingJobs]);

    const onHistoryDelete = useCallback((e, alert) => {
        e.preventDefault();
        e.stopPropagation();
        const handler = _get(tableAPIRef, 'current.state.handlers.onHistoryDelete');
        handler && handler(alert);
    }, [tableAPIRef]);

    const handleSearchFilterChange = useCallback(({ filter: f, search: s }) => {
        f !== undefined && setFilter(f);
        s !== undefined && setSearch(s);
    }, []);

    const handleSelectSavedFilter = useCallback(f => {
        setSavedFilter(f ? f.name : '');
        applyPageState(f ? f.data : f);
    }, [applyPageState]);

    const handleUpsertSavedFilter = useCallback(f => {
        setSavedFilter(f ? f.name : '');
        setFilter(_get(f, 'data.filter'));
        setSearch(_get(f, 'data.search'));
        const state = {
            ...getCurrentState(),
            filter: _get(f, 'data.filter'),
            search: _get(f, 'data.search')
        };
        AlertsService.upsertSavedFilter({ ...f, data: state })
            .then(res => setSavedFilters(res));
    }, [getCurrentState, AlertsService]);

    const handleDeleteSavedFilter = useCallback(f => {
        AlertsService.deleteSavedFilter(f.uuid)
            .then(res => setSavedFilters(res));
        setSavedFilter(current => current === f.name ? '' : current);
    }, [AlertsService]);

    const handleOnFilterImport = useCallback(data => {
        // backward compatibility
        if (data.data) {
            data = { ...data.data, type: data.type };
        }
        if (data.type !== 'alerts') {
            toast.error(`Cannot import '${data.type}' file into this view`);
            return;
        }
        setSavedFilter();
        applyPageState(data);
    }, [applyPageState]);

    const handleOnFilterExport = useCallback(f => {
        if (!f) return;

        const state = {
            ...getCurrentState(),
            filter: f.filter,
            search: f.search,
            type: 'alerts',
        };
        SaveFile(
            JSON.stringify(state),
            `${savedFilter || 'alerts-filter'}.json`,
            'text/json;charset=utf-8;'
        );
    }, [getCurrentState, savedFilter]);

    // provide a long debounce to give user a chance to type before failing for bad format
    const handleStartChange = useCallback(start => {
        if (!start || !start.isValid()) return;
        setQuick();
        setRange(current => [start ? start.utc().toISOString() : undefined, current[1]]);
    }, []);

    const handleEndChange = useCallback(end => {
        if (end && !end.isValid()) return;
        setQuick();
        setRange(current => [current[0], end ? end.utc().toISOString() : undefined]);
    }, []);

    const handleStartIncidents = useCallback(() => {
        tableAPIRef.current.selectRows(
            [...tableAPIRef.current.getSelectedRows().keys()],
            false
        );
        setSelection([]);
        setTagging(true);
    }, [tableAPIRef]);

    const handleIncidentSelection = useCallback((rows, incident) => {
        IncidentService.addAlerts(incident.uuid, rows.map(d => ({ uuid: d.uuid, timestamp: d.timestamp })));
    }, []);

    const handleFinishIncidents = useCallback(incident => {
        setTagging(false);
        if ((selection || []).length < 1) return;

        tableAPIRef.current.selectRows(selection, false);
        incident && handleIncidentSelection(selection.map(s => tableAPIRef.current.getRow(s)), incident);
    }, [tableAPIRef, selection, handleIncidentSelection]);

    const handleStartMitre = useCallback(() => {
        tableAPIRef.current.selectRows(
            [...tableAPIRef.current.getSelectedRows().keys()],
            false
        );
        setSelection([]);
        setTagging(true);
    }, [tableAPIRef]);

    const handleMitreSelection = useCallback((rows, mitre) => {
        if ((groups || []).length > 0) {
            setConfirmDlg({
                msg: "This may be a long operation. You will be notified when it's complete",
                callback: () => {
                    setConfirmDlg(false);
                    let f = {
                        operator: 'Or',
                        nodes: rows.map(a => AlertsService.filterFromGroups(a, groups)),
                    };
                    if (filter && (filter.nodes || []).length > 0) {
                        f = { operator: 'And', nodes: [filter, f] };
                    }
                    // async call
                    setAlertsMitre({ id: mitre }, {
                        search,
                        start: range[0],
                        end: range[1],
                        filter: f,
                        tagFilters,
                        tagsOp,
                        read,
                        backtesting,
                        selectedBTJobs,
                        jobCreated,
                        cmc,
                    }).catch(handleError);
                }
            });
        } else {
            rows.forEach(a => {
                setAlertMitre(a.uuid, mitre, cmc);
            });
        }
        // update visible rows immediately
        tableAPIRef.current.updateRows(rows.map(d => {
            d.data.bricata.mitre_category = mitre;
            return { ...d };
        }));
    }, [
        tableAPIRef,
        AlertsService,
        cmc,
        filter,
        groups,
        range,
        tagFilters,
        tagsOp,
        read,
        backtesting,
        selectedBTJobs,
        jobCreated,
        search,
        setAlertMitre,
        setAlertsMitre,
    ]);

    const handleFinishMitre = useCallback(mitre => {
        setTagging(false);
        if ((selection || []).length < 1) return;

        tableAPIRef.current.selectRows(selection, false);
        mitre && handleMitreSelection(selection.map(s => tableAPIRef.current.getRow(s)), mitre);
    }, [tableAPIRef, handleMitreSelection, selection]);

    const handleStartTagging = useCallback(() => {
        tableAPIRef.current.selectRows(
            [...tableAPIRef.current.getSelectedRows().keys()],
            false
        );
        setSelection([]);
        setTagging(true);
    }, [tableAPIRef]);

    const handleTagSelection = useCallback((rows, tag) => {
        if ((groups || []).length > 0) {
            setConfirmDlg({
                msg: "This may be a long operation. You will be notified when it's complete",
                callback: () => {
                    setConfirmDlg(false);
                    // toast.info("this operation can be lengthy and will continue in the background. You will be notified once it's complete");
                    let f = {
                        operator: 'Or',
                        nodes: rows.map(a => AlertsService.filterFromGroups(a, groups)),
                    };
                    if (filter && (filter.nodes || []).length > 0) {
                        f = { operator: 'And', nodes: [filter, f] };
                    }
                    // async call
                    AlertsService.tagAlerts(tag, {
                        search,
                        start: range[0],
                        end: range[1],
                        filter: f,
                        tagFilters,
                        tagsOp,
                        read,
                        backtesting,
                        selectedBTJobs,
                        jobCreated,
                        cmc,
                    }).catch(handleError);
                }
            });
        } else {
            rows.forEach(a => {
                AlertsService.tagAlert(tag, a, cmc);
            });
        }
        // update visible rows immediately
        tableAPIRef.current.updateRows(rows.map(d => {
            if (!_get(d, 'data.bricata.tag')) _set(d, 'data.bricata.tag', []);
            if (!d.data.bricata.tag.find(t => t === tag)) {
                d.data.bricata.tag.unshift(tag);
            }
            return { ...d };
        }));
    }, [
        tagFilters,
        tagsOp,
        read,
        backtesting,
        selectedBTJobs,
        jobCreated,
        cmc,
        search,
        tableAPIRef,
        AlertsService,
        filter,
        groups,
        range,
    ]);

    const handleFinishTagging = useCallback(tag => {
        setTagging(false);
        if ((selection || []).length < 1) return;

        tableAPIRef.current.selectRows(selection, false);
        tag && handleTagSelection(selection.map(s => tableAPIRef.current.getRow(s)), tag);
    }, [tableAPIRef, handleTagSelection, selection]);

    const handleTagAlert = useCallback((alert, tag) => {
        if (!tag) return;
        AlertsService.tagAlert(tag.name, alert, cmc);

        // update row immediately
        const r = tableAPIRef.current.getRow(alert.uuid);
        if (!_get(r, 'data.bricata.tag')) _set(r, 'data.bricata.tag', []);
        if (!r.data.bricata.tag.find(t => t === tag.name)) {
            r.data.bricata.tag.unshift(tag.name);
            tableAPIRef.current.updateRows([r]);
        }
    }, [AlertsService, tableAPIRef, cmc]);

    const handleUntagAlert = useCallback((alert, tag) => {
        if (!tag) return;
        AlertsService.untagAlert(tag, alert, cmc);

        // update row immediately
        const r = tableAPIRef.current.getRow(alert.uuid);
        const i = _get(r, 'data.bricata.tag', []).findIndex(t => t === tag);
        if (i >= 0) {
            r.data.bricata.tag.splice(i, 1);
            tableAPIRef.current.updateRows([r]);
        }
    }, [AlertsService, tableAPIRef, cmc]);

    const handleMitreFilter = async mitre => {
        setConfirmDlg({
            msg: "This may be a long operation. You will be notified when it's complete",
            callback: () => {
                setConfirmDlg(false);
                // async call
                setAlertsMitre(mitre, {
                    search,
                    start: range[0],
                    end: range[1],
                    filter,
                    tagFilters,
                    tagsOp,
                    read,
                    backtesting,
                    selectedBTJobs,
                    jobCreated,
                    cmc,
                }).catch(handleError);
            }
        });
    };

    const handleUnmitreFilter = async () => {
        setConfirmDlg({
            msg: "This may be a long operation. You will be notified when it's complete",
            callback: () => {
                setConfirmDlg(false);
                // async call
                clearAlertsMitre({
                    search,
                    start: range[0],
                    end: range[1],
                    filter,
                    tagFilters,
                    tagsOp,
                    read,
                    backtesting,
                    selectedBTJobs,
                    jobCreated,
                    cmc,
                }).catch(handleError);
            }
        });
    };

    const handleTagFilter = async tag => {
        setConfirmDlg({
            msg: "This may be a long operation. You will be notified when it's complete",
            callback: () => {
                setConfirmDlg(false);
                // async call
                AlertsService.tagAlerts(tag, {
                    search,
                    start: range[0],
                    end: range[1],
                    filter,
                    tagFilters,
                    tagsOp,
                    read,
                    backtesting,
                    selectedBTJobs,
                    jobCreated,
                    cmc,
                }).catch(handleError);
            }
        });
    };

    const handleUntagFilter = async tag => {
        setConfirmDlg({
            msg: "This may be a long operation. You will be notified when it's complete",
            callback: () => {
                setConfirmDlg(false);
                // async call
                AlertsService.untagAlerts(tag, {
                    search,
                    start: range[0],
                    end: range[1],
                    filter,
                    tagFilters,
                    tagsOp,
                    read,
                    backtesting,
                    selectedBTJobs,
                    jobCreated,
                    cmc,
                }).catch(handleError);
            }
        });
    };

    const handleTagSelect = tag => {
        const i = (tagFilters || []).findIndex(t => t === tag);
        if (i >= 0) return;
        setTagFilters([...(tagFilters || []), tag]);
    };

    // outgoing to grid
    const getSortModel = () => {
        const ss = sort.split(' ');
        if (ss.length < 2) return;

        if (!AlertsService.columnsMap[ss[0]]) {
            console.log(AlertsService.columnsMap[ss[0]], ' is not a valid field');
            return;
        }
        if (ss[1] !== 'asc' && ss[1] !== 'desc') {
            console.log(ss, ss[1], 'is not a valid sort order for', ss[0]);
            ss[1] = 'asc';
        }
        return [{ field: AlertsService.columnsMap[ss[0]].field, sort: ss[1] }];
    };

    // incoming from grid
    const handleOnSort = p => {
        if (!p.length) return;
        const f = AlertsService.fieldsMap[p[0].field];
        if (!f) return;
        setSort(`${f.variable} ${p[0].sort}`);
    };

    const handleFilterDest = value => {
        const nodes = value.map(v => ({ variable: 'dest_ip', value: v, type: 'ip' }));
        handleAddFilter([{ operator: 'Or', nodes, expanded: true }]);
    };

    const handleFilterSrc = value => {
        const nodes = value.map(v => ({ variable: 'src_ip', value: v, type: 'ip' }));
        handleAddFilter([{ operator: 'Or', nodes, expanded: true }]);
    };

    // [{ operator: 'And', root: true, nodes: f, expanded: true }];
    const handleAddFilter = useCallback(f => {
        if (!f || f.length < 1) return;

        // incoming job filters will just get added to the filtered backtesting jobs list
        const jobs = f.filter(ff => ff.type === 'bt_job_uuid').map(ff => ff.value);
        f = f.reduce((a, c) => c.type === 'bt_job_uuid' ? a : a.concat([c]), []);

        if (jobs.length > 0) {
            setSelectedBTJobs(current => {
                if (!current) return jobs;

                jobs.forEach(j => {
                    if (current.findIndex(c => c === j) < 0) current.push(j);
                });

                return jobs;
            });
        }

        if (!f || f.length < 1) return;

        setFilter(current => {
            if (current && (current.nodes || []).length > 0) {
                if (current.operator === 'And') {
                    const same = (c, n) =>
                        c.variable && n.variable === c.variable && n.value === c.value;
                    f = f.filter(n => current.nodes.findIndex(c => same(c, n)) < 0);
                    current.nodes.push(...f);
                    f = current.nodes;
                } else {
                    f.push(current);
                }
            }
            return { operator: 'And', root: true, nodes: f, expanded: true }; // uf;
        });
        if (f.length) {
            setShowFilterTimeout({ exit: 4200 });

            const timeoutId = setTimeout(() => {
                setShowFilterTimeout();
            }, 4200);
            return () => clearTimeout(timeoutId);
        }
    }, []);

    const handleSaveNote = (uuid, note) => {
        AlertsService.saveNote(uuid, note, cmc);

        // update row immediately
        const r = tableAPIRef.current.getRow(uuid);
        _set(r, 'data.bricata.note', note);
    };

    const handleDonutMouseover = useCallback(filter => {
        setGroupFilter(filter ? filter.key : filter);
    }, []);

    const handleDonutClick = useCallback(filter => {
        handleAddFilter(_get(filter, 'groups', []).map(g => (
            { variable: g.key, value: `${g.value}`, }
        )));
    }, [handleAddFilter]);

    const handleTimelineMouseover = useCallback(selection => {
        setTLSelection(selection);
    }, []);

    const handleTimelineClick = useCallback(selection => {
        if (!selection) return;
        setQuick();
        setRange([selection.start, selection.end]);
    }, []);

    const getSorted = useCallback(() => {
        const d = groups.length > 0 ? 'count desc' : 'timestamp desc';
        let ss = (sort || d).split(' ');
        // defense against the dark arts of sorting on count when we're not grouping
        if (groups.length < 1 && ss[0] === 'count') ss = d.split(' ');
        return ss;
    }, [sort, groups.length]);

    // recursive, will break on signal or when it runs out of data
    const fetchSparkline = useCallback(async (data, controller) => {
        if (data.length < 1) return;
        const d = data.slice(0);
        let f = AlertsService.filterFromGroups(d[0], groups);
        if (f && (f.nodes || []).length > 0) {
            f = { operator: 'And', nodes: [filter, f] };
        }

        // skip empty timelines
        if (d[0].count <= 1) {
            // next!
            if (d.length > 0) {
                return fetchSparkline(d.slice(1), controller);
            }
            return;
        }

        return AlertsService.getTimeline({
            search,
            start: range[0],
            end: range[1],
            filter: f,
            tagFilters,
            tagsOp,
            // read, -- timeline doesn't filter on read/unread alerts
            backtesting,
            selectedBTJobs,
            jobCreated,
            groups,
            controller,
            cmc,
        }).then(res => {
            const uuid = _get(d[0], 'uuid');
            const row = tableAPIRef.current.getRow(uuid);
            tableAPIRef.current.updateRows([{ ...row, timeline: res }]);

            // tableAPIRef.current.updateRows([{ id: uuid, timeline: res }]);
            // next! (if the original query hasn't been canceled)
            if (!controller.signal.aborted && d.length > 0) {
                return fetchSparkline(d.slice(1), controller);
            }
        }).catch(err => {
            // this is a happy path - we're canceling outdated requests all the time
            if (err.code === DOMException.ABORT_ERR) return;
            console.error('fetchSparkline error', err);
        });
    }, [
        tableAPIRef,
        filter,
        search,
        range,
        tagFilters,
        backtesting,
        selectedBTJobs,
        jobCreated,
        tagsOp,
        groups,
        cmc,
        AlertsService,
    ]);

    const handleError = error => {
        // this is a happy path - we're canceling outdated requests all the time
        if (error.code === DOMException.ABORT_ERR) return;

        setLoading(false);
        // throttle refresh rate. will be reset when we have a successful fetch.
        setRefreshRate(current => current * 2);
        // stop surfacing up errors
        // toast.error(`There was an error processing your request, ${error}`);
        console.error('There was an error processing your request', _get(error, 'error', error));
    };

    const handleColumnsUpdated = ({ columns, sort, mappings }) => {
        setMappings(mappings);

        // if we're in history view the 'remove column' should be visible
        const r = columns.findIndex(c => c.variable === 'remove');
        if (r >= 0) columns[r].hide = currentMode !== HistoryMode;

        setColumns(columns);

        // might be a new column - trust the process
        if (sort && AlertsService.fieldsMap[sort.field]) {
            setSort(`${AlertsService.fieldsMap[sort.field].variable} ${sort.sort}`);
        }

        // if we're grouping and user chose to hide one of the grouping columns,
        // then we want to make that choice sticky
        if (groups.length > 0) {
            const hideColumns = columns
                .filter(c => c.grouping && c.hide)
                .reduce((a, c) => {
                    return { ...a, [c.variable]: true };
                }, {});
            setHideColumns(hideColumns);
        }
    };

    const getHistoryData = useCallback(async () => {
        const profileData = getProfile(profile);
        const history = (profileData.lastViewed || []);

        // first time with the new GUI, fetching history will be slow
        // after that we'll start using timestamp to limit the query
        let updated = false;
        let start = history.reduce((a, c) => {
            updated = updated || !c.timestamp;
            const ct = c.timestamp ? Moment(c.timestamp) : Moment().subtract(1, 'years');
            return !a || ct.isBefore(a) ? ct : a;
        }, false);
        start = (start || Moment().subtract(1, 'years')).utc().toISOString();
        updated && updateProfile(profile, { ...profileData, lastViewed: history });
        const ss = getSorted();

        const res = await AlertsService.getSelectAlerts({
            start,
            sort: `${ss[0]} ${ss[1]}`,
            uuids: history.map(h => h.uuid),
            cmc,
        }).catch(handleError);

        const data = (_get(res, 'data', [])).map(r => ({ ...r, id: r.uuid }));
        // backward compatibility one time only
        // add timestamp history to speed things up for the next times
        if (history.length > 0 && !history[0].timestamp) {
            history.forEach(h => {
                const a = data.find(a => a.uuid === h.uuid);
                if (a) h.timestamp = a.timestamp;
            });
            updateProfile(profile, { ...profileData, lastViewed: history });
        }
        setData(data);
        setLoading(false);
    }, [getProfile, updateProfile, getSorted, AlertsService, cmc, profile]);

    const startGeoFeed = useCallback(({ start }) => {
        setGeoHeartbeat(0);
        AlertsService.stopGeofeed();
        AlertsService.startGeofeed({
            search,
            start,
            filter,
            tagFilters,
            tagsOp,
            read,
            backtesting,
            selectedBTJobs,
            jobCreated,
            cmc,
        },
        res => {
            setRefresh(current => res.end || current);
            let nData = (res || {});
            setGeodata(cdata => {
                nData = MergeNodes(cdata || {}, nData);
                nData.clear = false;
                nData.live = true;
                return nData;
            });
        },
        () => setGeoHeartbeat(current => current + 1),
        err => console.error('Geo feed error', err));
    }, [
        filter,
        search,
        tagFilters,
        tagsOp,
        cmc,
        read,
        backtesting,
        selectedBTJobs,
        jobCreated,
        AlertsService,
    ]);

    const handleGeoPause = useCallback((pause, refresh) => {
        setPause(!pause);
        setGeoHeartbeat(0);
        if (pause) startGeoFeed({ start: refresh });
        else AlertsService.stopGeofeed();
    }, [startGeoFeed, AlertsService]);

    // recursive paging backwards until done
    const getGeoData = useCallback(() => {
        setLoading(true);
        AlertsService.getGeoHistory({
            search,
            start: range[0],
            end: range[1],
            filter,
            tagFilters,
            tagsOp,
            read,
            backtesting,
            selectedBTJobs,
            jobCreated,
            cmc,
        }).then(res => {
            console.debug('got geodata', 'end:', res.end);
            setRefresh(current => res.end || current);
            setGeodata({ ...res, clear: true, live: false });

            // live feed?
            if (!range[1]) {
                // this is a backdoor way of checking for current value of 'pause'
                // without triggering the callback to get updated when pause gets updated
                setPause(current => {
                    if (!current) startGeoFeed({ start: res.end });
                    return current;
                });
            }

            res.controller && res.controller.signal.addEventListener('abort', () => {
                console.debug('aborted', res.controller.signal);
                setGeoHeartbeat(0);
                AlertsService.stopGeofeed();
            });
        }, handleError).finally(() => setLoading(false));
    }, [
        filter,
        range,
        search,
        tagFilters,
        tagsOp,
        read,
        backtesting,
        selectedBTJobs,
        jobCreated,
        cmc,
        startGeoFeed,
        AlertsService]);

    const getData = useCallback(({ page, refresh }) => {
        setLoading(refresh || true);
        setDone(false);
        const ss = getSorted();
        // this is here to force a dependency
        console.debug('mappings', mappings);
        AlertsService.getData({
            sort: `${ss[0]} ${ss[1]}`,
            search,
            start: refresh || range[0],
            end: range[1],
            filter,
            tagFilters,
            tagsOp,
            groups,
            read,
            backtesting,
            selectedBTJobs,
            jobCreated,
            page: page || 1,
            cmc,
        }).then(res => {
            setRefreshRate(REFRESH_RATE);
            // track latest query timestamp for next refresh loop
            if (!page) {
                setRefresh(current => res.asof || current);
                setAsof(res.asof);
            }
            let nData = (res.data || []);
            if (nData.length < 1 && !refresh) {
                setDone(true);
                return;
            }
            setData(cdata => {
                if (refresh) {
                    // add to the top of the list
                    nData.push(...cdata);
                } else if (page) {
                    // add to the bottom of the list
                    nData.unshift(...cdata);
                }
                // no mas, and prune excess (good for refresh loop)
                if (nData.length > ALERTS_LIMIT) {
                    setDone(true);
                    nData.length = ALERTS_LIMIT;
                }
                // dedupe
                const seen = {};
                nData = nData.filter((a, i) => {
                    if (seen[a.uuid]) {
                        return false;
                    }
                    seen[a.uuid] = true;
                    return true;
                });
                return nData;
            });

            // if we have groups we can use this call for both the donut and the table
            if (groups.length > 0) {
                if (!_get(hideColumns, 'activity', false)) {
                    fetchSparkline(res.data, res.controller);
                }
            }
        }, handleError).catch(handleError).finally(() => setLoading(false));
    }, [
        fetchSparkline,
        filter,
        getSorted,
        groups,
        range,
        read,
        backtesting,
        selectedBTJobs,
        jobCreated,
        search,
        tagFilters,
        tagsOp,
        cmc,
        hideColumns,
        AlertsService,
        mappings,
    ]);

    const handleRemoveTagFilter = useCallback(tag => {
        setTagFilters(current => {
            const i = (current || []).findIndex(t => t === tag);
            if (i < 0) return current;
            current.splice(i, 1);
            return [...current];
        });
    }, []);

    const handleOnWantPolicySelect = policy => {
        setWantPolicy(current => ({ ...current, policy }));
    };

    const handleOnWantPolicy = () => {
        handleTuneAlert(wantPolicy.filter, wantPolicy.policy);
        setWantPolicy();
    };

    // use page filter
    const handleTune = useCallback(() => {
        if (!gatorPerms.read) return;

        PolicyService.getPolicies('data_tags').then(res => {
            const policies = (res || []).filter(p => {
                return MC2CHECK || p.source === 'cmc';
            });
            if (policies.length < 1) return;
            setWantPolicy({
                filter,
                policies: policies.map(p => p.name),
                policy: policies[0].name,
            });
        });
    }, [filter]);

    const handleTuneAlert = useCallback(async (filter, policy) => {
        const gator = await PolicyService.filterToGator(filter || EMPTY_FILTER);
        const seedGator = {
            gator,
            action: 'forget',
            scope: 'in',
            target: 'alert',
        };
        navigate(`/policies/sensor/data_tuning/${policy}?${ParamsSerializer(seedGator)}`);
    }, [navigate]);

    const handleCmcsSelect = useCallback(c => {
        setCmc(current => {
            // don't kick data if we don't need to
            if (current && current.uuid === c) return current;
            return getCmc(c);
        });
    }, [setCmc, getCmc]);

    // remove receives just uuid
    const handleCmcsRemove = useCallback(c => {
        if (!c) return;
        setSelectedCmcs(current => {
            const i = (current || []).findIndex(cc => cc === c);
            if (i < 0) return current;
            current.splice(i, 1);
            return [...current];
        });
    }, []);

    // to confuse the enemy, add recieves an object
    const handleCmcsAdd = useCallback(c => {
        if (!c) return;
        setSelectedCmcs(current => {
            const i = (current || []).findIndex(cc => cc === c.uuid);
            if (i >= 0) return current;
            return [...(current || []), c.uuid];
        });
    }, []);

    // update sort when grouping is reset
    useEffect(() => {
        setSort(current => {
            let nSort = current;
            if (groups && groups.length > 0) {
                nSort = 'count desc';
            }
            if (!groups || groups.length < 1) {
                nSort = 'timestamp desc';
            }
            return nSort;
        });
    }, [groups]);

    // try to keep current sort when mode changes
    useEffect(() => {
        setSort(current => {
            const cs = current.split(' ');
            let ss;
            if (groups && groups.length > 0) {
                ss = safeSort(cs[0], cs[1], ['count', 'desc']);
            } else {
                // defense against the dark arts of count sorting when we're not grouping
                if (currentMode === HistoryMode) {
                    ss = safeSort(cs[0], cs[1], ['remove', 'desc']);
                } else {
                    ss = safeSort(cs[0], cs[1], defaultSort.split(' '));
                }
            }

            return `${ss[0]} ${ss[1]}`;
        });
    }, [groups, safeSort, currentMode, defaultSort]);

    // cmc selections (open tabs) hook when cmc list changes
    useEffect(() => {
        if ((cmcs || []).length === 0) {
            setSelectedCmcs([]);
            return;
        }
        setSelectedCmcs(current => {
            if ((current || []).length === 0) return current;
            return current.filter(sc => (cmcs || {})[sc]);
        });
    }, [cmcs]);

    // cmc hook when cmc selection list changes
    useEffect(() => {
        if ((selectedCmcs || []).length === 0) {
            setCmc();
            return;
        }
        setCmc(current => {
            if (!current) return getCmc(selectedCmcs[0]);
            if (!selectedCmcs.find(c => c === current.uuid)) return getCmc(selectedCmcs[0]);
            return current;
        });
    }, [selectedCmcs, setCmc, getCmc]);

    // selected cmc hook when cmc list changes
    useEffect(() => {
        if (cmc) {
            if (!(cmcs || {})[cmc.uuid]) return;
            setSelectedCmcs(current => {
                if (!(current || []).find(c => c === cmc.uuid)) {
                    // add cmc to the tab list
                    return [...(current || []), cmc.uuid];
                }
                return current;
            });
        }
    }, [cmc, cmcs, AlertsService]);

    const handleSaveReport = useCallback(async format => {
        const settings = await ReportsService.getReportMaxRows().catch(handleError);
        toastId.current = toast(<ReportsToast
            settings={settings}
            format={format}
            total={total}/>, { autoClose: false });
        AlertsService.generateReport({
            sort,
            search,
            start: range[0],
            end: range[1],
            filter,
            tagFilters,
            tagsOp,
            groups,
            read,
            backtesting,
            selectedBTJobs,
            jobCreated,
            columns: columns.filter(c => !c.hide).map(c => c.variable),
            format,
            total,
            cmc,
        }).then(res => {
            toast.dismiss(toastId.current);
        }, err => {
            toast.dismiss(toastId.current);
            if (err.status === 502) {
                toast.info("We'll continue working on your report. When it's done it will be available from the Reports page in the System menu.");
            } else {
                console.log(err);
                toast.error("We're unable to process your report.");
            }
        }).catch(handleError).finally(() => toast.dismiss(toastId.current));
    }, [
        total,
        columns,
        sort,
        search,
        range,
        filter,
        tagFilters,
        tagsOp,
        groups,
        read,
        backtesting,
        selectedBTJobs,
        jobCreated,
        cmc,
        AlertsService,
    ]);

    const handleShare = useCallback((p) => {
        const params = p && p.uuid
            ? {
                filter: JSON.stringify({
                    operator: 'And',
                    nodes: [{ variable: 'event_uuid', value: p.uuid }]
                }),
                quick,
                start: range[0],
                end: range[1],
                cmc: (cmc || {}).uuid,
            }
            : {
                filter: filter ? JSON.stringify(filter) : undefined,
                groups: groups.length > 0 ? (groups || []).map(g => g.variable) : undefined,
                search,
                sort,
                tagFilters,
                tagsOp,
                quick,
                read,
                backtesting,
                selectedBTJobs,
                start: range[0],
                end: range[1],
                cmc: (cmc || {}).uuid,
            };
        CopyToClipboard(`${window.location.protocol}//${window.location.host}/views/alerts?${ParamsSerializer(params)}`);
        toast.info('Link was copied to clipboard', { progress: undefined });
    }, [
        filter,
        range,
        groups,
        search,
        sort,
        tagFilters,
        tagsOp,
        quick,
        read,
        backtesting,
        selectedBTJobs,
        cmc,
    ]);

    // new data poller. pause if live data isn't available, or user paused or query is already running
    const isLive = useCallback(() => {
        if (!init || range[1]) return false;
        if (!tabVisible) return false;
        if (currentMode === ListMode) return sort === 'timestamp desc' && groups.length < 1;
        return false;
    }, [init, groups.length, sort, currentMode, range, tabVisible]);

    // conditions for live data
    useInterval(() => {
        getData({ refresh });
        // update timeline and donut
        if (!MC2CHECK) {
            (async () => {
                const start = timeline.length > 0 ? timeline[timeline.length - 1].start : refresh;
                const d = await AlertsService.getTimeline({
                    search,
                    start,
                    end: range[1],
                    filter,
                    tagFilters,
                    tagsOp,
                    groups,
                    interval,
                    backtesting,
                    selectedBTJobs,
                    jobCreated,
                    cmc,
                }).catch(handleError);
                if (d) {
                    let ntotal = total;
                    (d.objects || []).forEach(n => {
                        const o = (timeline || []).find(oo => oo.start === n.start);
                        if (o) {
                            // update
                            ntotal += (+n.count) - (+o.count);
                            o.count = n.count;
                            o.groups = n.groups;
                        } else {
                            // add
                            ntotal += n.count;
                            timeline.push(n);
                        }
                    });
                    setTimeline([...timeline]);
                    setTotal(ntotal);
                    setGroupedData(AlertsService.aggregate(timeline));
                }
            })();
        }
    }, (!pause && !loading && props.live !== false && isLive()) ? refreshRate : null);

    const readRouteQuery = useCallback(() => {
        const routeQuery = new URLSearchParams(location.search);
        if (routeQuery.toString().length < 1) return {};
        const state = {};

        // making route params backward compatible so existing links still work
        if (routeQuery.has('json_filter')) state.filter = routeQuery.get('json_filter');
        else state.filter = routeQuery.get('filter');
        if (routeQuery.has('group')) state.groups = routeQuery.getAll('group');
        else state.groups = routeQuery.getAll('groups');
        if (routeQuery.has('tags')) state.tagFilters = routeQuery.getAll('tags');
        else state.tagFilters = routeQuery.getAll('tagFilters');
        if (routeQuery.has('tags_op')) state.tagsOp = routeQuery.get('tags_op');
        else state.tagsOp = routeQuery.get('tagsOp');
        if (routeQuery.has('start_time')) state.start = routeQuery.get('start_time');
        else state.start = routeQuery.get('start');
        if (routeQuery.has('end_time')) state.end = routeQuery.get('end_time');
        else state.end = routeQuery.get('end');
        state.quick = routeQuery.get('quick');
        state.search = routeQuery.get('search');
        state.sort = routeQuery.get('sort');
        state.cmc = routeQuery.get('cmc');
        if (state.cmc) state.selectedCmcs = [state.cmc];
        if (routeQuery.has('backtesting')) state.backtesting = parseInt(routeQuery.get('backtesting'), 10);
        if (routeQuery.has('selectedBTJobs')) state.selectedBTJobs = routeQuery.getAll('selectedBTJobs');

        // don't keep nulls
        if (!state.search) state.search = '';
        if (!state.filter) state.filter = '';
        if (!state.sort) state.sort = state.groups.length ? 'count desc' : 'timestamp desc';
        if (!state.start) delete state.start;
        if (!state.end) delete state.end;
        if (!state.cmc) delete state.cmc;

        try {
            state.filter = JSON.parse(state.filter);
        } catch (err) {
            console.log('ignoring filter param', routeQuery.get('filter'));
            state.filter = EMPTY_FILTER;
        }

        if (_get(state.filter, 'nodes.length', 0) > 0) {
            // expand all nodes
            const expand = f => {
                (f.nodes || []).forEach(expand);
                if (f.operator) f.expanded = true;
            };
            expand(state.filter);

            setShowFilterTimeout({ exit: 4200 });
            // reset timeout back to zero so we can use this again
            setTimeout(() => {
                setShowFilterTimeout();
            }, 4200);
        }
        return state;
    }, [location.search]);

    const toggleFilterbar = useCallback(() => {
        setHideFilterbar(current => !current);
    }, []);

    const toggleTimelinebar = useCallback(() => {
        setHideTimelinebar(current => !current);
    }, []);

    const handleColumnReorder = useCallback(params => {
        setColumns(tableAPIRef.current.getAllColumns());
    }, [tableAPIRef]);

    const handleColumnResize = useCallback(params => {
        setWidths(current => {
            return {
                ...current,
                [params.colDef.variable]: params.width,
            };
        });
    }, []);

    const handleScrollEnd = useCallback(params => {
        // paging takes priority over refresh.
        const pagers = { list: true, threetuple: true, fivetuple: true };
        if (!done && pagers[currentMode]) {
            setPage(p => p + 1);
        }
    }, [done, currentMode]);

    const maybeReturnToSelection = useCallback(() => {
        if (mode.length < 3) return false;

        const currentMode = mode[mode.length - 1];
        if (currentMode === ListMode || currentMode === HistoryMode) {
            const prevMode = mode[mode.length - 2];
            if (prevMode === DetailMode) {
                // are we returning from the same place we came from?
                if (mode[mode.length - 3] === currentMode && currentAlert) {
                    tableAPIRef.current.scrollToIndexes({ rowIndex: currentAlert.rowIndex });
                    return true;
                }
            }
        }
        return false;
    }, [mode, currentAlert, tableAPIRef]);

    // english hook
    useEffect(() => {
        const bt = GetBacktestingEnglish(selectedBTJobs, backtestingJobsLite);

        const sf = savedFilter ? `Saved Filter: ${savedFilter}` : '';

        const f = AlertsService.toEnglish(filter, true, sensorNames, assets, backtestingJobsLite);

        const combined = [sf, f, bt]
            .filter(f => f.length && f !== '<span></span>')
            .join(' <b>AND</b> ');
        setEnglish(combined);
    }, [
        filter,
        savedFilter,
        sensorNames,
        assets,
        AlertsService,
        backtestingJobsLite,
        selectedBTJobs,
    ]);

    useEffect(() => {
        // update backtesting bit when data becomes available
        const btui = _get(systemSettings, 'support.backtesting.ui', false);

        setBacktesting(current => {
            return current === BTMODE_NONE
                ? btui ? BTMODE_LIVE : BTMODE_NONE
                : current;
        });
    }, [(systemSettings)]);

    const getRowId = useCallback(r => r.uuid, []);

    // initial hook
    useEffect(() => {
        (async () => {
            setFields(AlertsService.fields);
        })();

        if (!props.hideFilterbar) {
            (async () => {
                AlertsService.cacheSavedFilters()
                    .then(d => setSavedFilters(d))
                    .catch(handleError);
            })();
        }

        // check for incidents
        if (!MC2CHECK) {
            (async () => {
                !props.miniToolbar && incidentPerms.write && IncidentService.getAll()
                    .then(res => setHaveIncidents((res || []).length > 0));
            })();
        }

        if (MC2CHECK) {
            if (cmcPerms.read) {
                (async () => {
                    CMCsDataService.getNames().then(cmcs => setCmcs(cmcs));
                })();
            }
        }

        if (gatorPerms.read) {
            (async () => {
                PolicyService.getPolicies('data_tags').then(res => {
                    const policies = (res || []).filter(p => {
                        return MC2CHECK || p.source === 'cmc';
                    });
                    setCanTune(policies.length > 0);
                });
            })();
        } else {
            setCanTune(false);
        }

        return () => {
            setInit(false);
            AlertsService.cancelAll();
        };
    }, [AlertsService, props.miniToolbar, props.hideFilterbar]);

    // size hook
    useEffect(() => {
        // I <3 pixel pushing
        const fb = hideFilterbar ? 15 : 80;
        const tb = hideTimelinebar ? 15 : TIMELINE_HEIGHT;
        const both = MC2CHECK && hideFilterbar ? -10
            : hideFilterbar && hideTimelinebar ? -15 : 0;
        const footer = 23;
        setTableHeight(Math.max(
            height - (props.headerSize || (PAGE_HEADER + fb + tb + both)) - footer,
            200
        ));
    }, [height, hideFilterbar, hideTimelinebar, props.headerSize]);

    // once alerts fields are cached we can set the rest of the page
    useEffect(() => {
        // boostrap
        if (fields.length < 1) return;
        if (MC2CHECK && !cmcs) return;

        // do this only once
        if (init) return;
        const profileData = getProfile(profile);
        const state = _get(profileData, 'state', { quick: QUICK_HOUR });
        const params = readRouteQuery();

        // query parameters take priority, but state has column preferences
        applyPageState({ ...state, ...params });
        setHideFilterbar(_get(profileData, 'hideFilterbar', false));
        setHideTimelinebar(_get(profileData, 'hideTimelinebar', false));
        setInit(true);
    }, [
        AlertsService,
        init,
        fields,
        getProfile,
        readRouteQuery,
        applyPageState,
        cmcs,
        profile,
    ]);

    // visibleColumns only change when absolutely necessary.
    // reduce side effects and spamming profile settings
    useEffect(() => {
        const nvm = columns
            .filter(c => !c.hide)
            .reduce((a, c, i) => ({ ...a, [c.variable]: { order: i } }), {});

        setVisibleColumns(current => {
            if (Object.keys(nvm).length !== Object.keys(current || {}).length) return nvm;

            const same = Object.entries(current || {}).reduce((a, [k, v], i) => {
                return a && !!(nvm[k]) && nvm[k].order === v.order;
            }, true);

            return same ? current : nvm;
        });
    }, [columns]);

    // donut update hook
    useEffect(() => {
        if (!init) return;
        const d = tlselection ? AlertsService.aggregate([tlselection]) : (groupedData || []);
        setDonutData(d);
        setDonutFilter(AlertsService.timestamp(tlselection));
    }, [tlselection, init, groupedData, AlertsService]);

    // query changed hook for the main timeline
    useEffect(() => {
        if (!init) return;
        if (MC2CHECK) return;

        (async () => {
            setTimeline([]);
            setTotal(0);
            setGroupedData([]);
            const d = await AlertsService.getTimeline({
                search,
                start: range[0],
                end: range[1],
                filter,
                tagFilters,
                tagsOp,
                // read, timeline doesn't filter on read/unread
                backtesting,
                selectedBTJobs,
                jobCreated,
                groups,
                cmc,
            }).catch(handleError);
            if (d) {
                // set some derived data
                setTimeline(d.objects);
                setTotal(d.count);
                setGroupedData(AlertsService.aggregate(d.objects) || []);
                setInterval(d.interval);
            }
        })();
    }, [
        init,
        search,
        range[0],
        range[1],
        filter,
        tagFilters,
        tagsOp,
        groups,
        cmc,
        AlertsService,
        backtesting,
        selectedBTJobs,
        jobCreated,
    ]);

    // mode changed hook for data (donut and table)
    useEffect(() => {
        if (!init || !_get(tableAPIRef, 'current') || currentMode === DetailMode || currentMode === HistoryMode) return;
        if (maybeReturnToSelection()) return;

        // kick the data query to trigger the hook below
        setFilter(current => ({ ...(current || {}) }));
    }, [init, tableAPIRef, currentMode, maybeReturnToSelection]);

    // query changed hook for data (donut and table)
    useEffect(() => {
        if (!init || !_get(tableAPIRef, 'current')) return;

        setLoading(true);
        setCurrentAlert();
        setNextAlert();
        setPrevAlert();
        setData([]);
        setGeodata({});
        setPage(1);
        setDone(false);
        AlertsService.stopGeofeed();
        setGeoHeartbeat(0);

        // don't add currentMode to dependencies, we don't want to trigger this when mode changes
        // the hook above will take care of that if needed
        if (currentMode === MapMode) {
            getGeoData({});
        } else {
            getData({});
        }
    }, [init, tableAPIRef, getData, getGeoData, AlertsService]);

    // query changed hook for history
    useEffect(() => {
        if (!init || !_get(tableAPIRef, 'current') || currentMode !== HistoryMode) return;
        if (maybeReturnToSelection()) return;

        setLoading(true);
        setCurrentAlert();
        setNextAlert();
        setPrevAlert();
        setData([]);
        setGeodata({});
        setPage(1);
        setDone(false);
        AlertsService.stopGeofeed();
        setGeoHeartbeat(0);
        getHistoryData();
    }, [init, tableAPIRef, currentMode, maybeReturnToSelection, getHistoryData, AlertsService]);

    // next page hook
    const prevPage = usePrevious(page);
    useEffect(() => {
        if (!page || page < 2 || page === prevPage) return;
        getData({ page });
    }, [page, getData, prevPage]);

    // hook for updating table components, e.g., toolbar, footer
    useEffect(() => {
        if (!init) return;
        // update table ref for use by grouping toolbar
        tableAPIRef.current.state.params = {
            groups,
            mode: currentMode,
            tagFilters,
            tagsOp,
            grouping,
            read,
            total,
            asof,
            pause,
            fields: AlertsService.fieldsMap,
            cmcs,
            selectedCmcs,
            cmc,
            profile: 'alerts',
        };
        if (_get(tableAPIRef, 'current')) {
            // add handlers for toolbar
            tableAPIRef.current.state.handlers = {
                onTagAction: () => { setShowTags(true); setShowMitre(false) },
                onRemoveTag: currentMode === HistoryMode ? undefined : handleRemoveTagFilter,
                onTagsOpSelect: currentMode === HistoryMode ? undefined : setTagsOp,
                onColumnsSel: () => setShowColumnsSel(true),
                onMitreAction: incidentPerms.write ? () => { setShowMitre(true); setShowTags(false) } : undefined,
            };

            if (!props.miniToolbar) {
                tableAPIRef.current.state.handlers = {
                    ...tableAPIRef.current.state.handlers,
                    onAddGroup: currentMode === HistoryMode ? undefined : g => {
                        // reset sorting when grouping changes
                        if (groups.length < 1) safeSetSort('count', 'desc');
                        // defense against the dark arts
                        if (groups.find(gg => gg.variable === g.variable)) return;
                        setGroups([...groups, g]);
                        // get out of tuple view
                        setMode(current => {
                            const pMode = current.length > 0 ? current[current.length - 1] : ListMode;
                            return pMode === ThreetupleMode || pMode === FivetupleMode ? [...current, ListMode] : current;
                        });
                    },
                    onClearGroups: currentMode === HistoryMode ? undefined : () => {
                        const ds = defaultSort.split(' ');
                        safeSetSort(ds[0], ds[1]);
                        setGroups(current => (current && current.length > 0) ? [] : current);
                        // get out of tuple view
                        setMode(current => {
                            const pMode = current.length > 0 ? current[current.length - 1] : ListMode;
                            return pMode === ThreetupleMode || pMode === FivetupleMode ? [...current, ListMode] : current;
                        });
                    },
                    onRemoveGroup: currentMode === HistoryMode ? undefined : g => {
                        setGroups(current => {
                            const i = (current || []).findIndex(c => g.variable === c.variable);
                            if (i < 0) return current;
                            current.splice(i, 1);
                            return [...current];
                        });

                        // get out of tuple view
                        setMode(current => {
                            const pMode = current.length > 0 ? current[current.length - 1] : ListMode;
                            return pMode === ThreetupleMode || pMode === FivetupleMode ? [...current, ListMode] : current;
                        });
                    },
                    onIncidentsAction: haveIncidents && (groups || []).length < 1 && currentMode === ListMode
                        ? () => setShowIncidents(true) : undefined,
                    onGrouping: currentMode === HistoryMode ? undefined : setGrouping,
                    onRead: currentMode === HistoryMode ? undefined : setRead,
                    onHistoryDelete: row => {
                        const profileData = getProfile(profile);
                        const history = (profileData.lastViewed || []);
                        const i = history.findIndex(h => h.uuid === row.uuid);
                        if (i >= 0) history.splice(i, 1);
                        updateProfile(profile, { ...profileData, lastViewed: history });

                        // remove from list immediately
                        setData(current => {
                            const i = current.findIndex(d => d.uuid === row.uuid);
                            // something something... non-configurable... something something...
                            const ret = [...current];
                            ret.splice(i, 1);
                            return ret;
                        });
                    },
                    onReport: (currentMode === DetailMode || currentMode === HistoryMode) ? undefined : handleSaveReport,
                    onTune: (currentMode === DetailMode || currentMode === HistoryMode || !gatorPerms.write || !canTune)
                        ? undefined : handleTune,
                    onShare: (currentMode === DetailMode || currentMode === HistoryMode) ? undefined : handleShare,
                    onPause: isLive() ? () => setPause(current => !current) : undefined,
                    onFetch: (currentMode === DetailMode || currentMode === HistoryMode || currentMode === MapMode || range[1]) ? undefined
                        : sort !== 'timestamp desc' || groups.length > 0
                            ? () => getData({})
                            : () => getData({ refresh }),
                    onCmcSelect: MC2CHECK ? handleCmcsSelect : undefined,
                    onCmcRemove: MC2CHECK ? handleCmcsRemove : undefined,
                    onCmcsAdd: MC2CHECK ? handleCmcsAdd : undefined,
                };
            }
        }
        // disabled 'view alert' bubble when grouping
        const detail = modes.findIndex(m => m.key === DetailMode);
        modes[detail].disabled = (groups && groups.length > 0) || currentMode === MapMode;
    }, [
        init,
        tableAPIRef,
        currentMode,
        getProfile,
        updateProfile,
        safeSetSort,
        defaultSort,
        groups,
        tags,
        tagsOp,
        tagFilters,
        grouping,
        read,
        total,
        asof,
        pause,
        range,
        sort,
        isLive,
        getData,
        refresh,
        handleRemoveTagFilter,
        handleTune,
        handleShare,
        handleSaveReport,
        // gatorPerms,
        haveIncidents,
        // incidentPerms,
        cmc,
        cmcs,
        selectedCmcs,
        handleCmcsSelect,
        handleCmcsRemove,
        handleCmcsAdd,
        props.miniToolbar,
        AlertsService,
        profile,
        canTune,
    ]);

    // hook for updating group colors and count bar
    useEffect(() => {
        setMaxGroup(groupedData.reduce((a, c) => a > c.count ? a : c.count, 0));
        setGroupKeys(Array.from(new Set([...Severities, ...groupedData.map(g => g.key)])));
    }, [groupedData]);

    // hook for responding to group changes (show/hide columns and sort)
    useEffect(() => {
        if (!tableAPIRef.current) return;
        const headerRenderers = getHeaderRenderers();
        const cellRenderers = getCellRenderers();

        // show/hide grouping columns
        setColumns(current => {
            // should return default here?
            if (current.length < 1) return current;

            // don't force a re-render unless something changes
            let changed = false;

            let cols = [...current];

            // show/hide backtesting columns
            const b = cols.findIndex(c => c.variable === 'job_uuid');
            if (b >= 0) {
                const hide = backtesting !== BTMODE_BT;
                if (cols[b].hide !== hide) {
                    cols[b].hide = backtesting !== BTMODE_BT;
                    cols = arrayMove(cols, b, 0);
                    changed = true;
                }
            }

            const ts = cols.findIndex(c => c.variable === 'timestamp');
            if (ts >= 0) {
                const title = backtesting !== BTMODE_BT ? 'Timestamp' : 'Orig Time';
                if (cols[ts].title !== title) {
                    cols[ts].title = title;
                    changed = true;
                }
            }

            const dbt = cols.findIndex(c => c.variable === 'db_timestamp');
            if (dbt >= 0) {
                const chide = backtesting !== BTMODE_BT;
                if (cols[dbt].columnSelectorHidden !== chide) {
                    cols[dbt].columnSelectorHidden = chide;
                    changed = true;
                }
                const hide = backtesting !== BTMODE_BT || hideColumns.db_timestamp;
                if (cols[dbt].hide !== hide) {
                    cols[dbt].hide = hide;
                    changed = true;
                }
            }

            const a = cols.findIndex(c => c.variable === 'activity');
            if (a >= 0) {
                let move = false;
                const hide = groups.length < 1 || currentMode === HistoryMode || !!(hideColumns.activity);
                if (cols[a].hide !== hide) {
                    cols[a].hide = hide;
                    move = true;
                }
                const chide = groups.length < 1;
                if (cols[a].columnSelectorHidden !== chide) {
                    cols[a].columnSelectorHidden = chide;
                    move = true;
                }
                if (move) {
                    cols = arrayMove(cols, a, 0);
                    changed = true;
                }
            }

            const c = cols.findIndex(c => c.variable === 'count');
            if (c >= 0) {
                let move = false;
                const hide = groups.length < 1 || currentMode === HistoryMode || hideColumns.count;
                if (cols[c].hide !== hide) {
                    cols[c].hide = hide;
                    move = true;
                }
                const chide = groups.length < 1;
                if (cols[c].columnSelectorHidden !== chide) {
                    cols[c].columnSelectorHidden = groups.length < 1;
                    move = true;
                }
                if (move) {
                    cols = arrayMove(cols, c, 0);
                    changed = true;
                }
            }

            const r = cols.findIndex(c => c.variable === 'remove');
            if (r >= 0) {
                cols[r].renderCell = p =>
                    <IconButton size="small"
                        style={{ paddingLeft: 0 }}
                        onClick={e => onHistoryDelete(e, p.row)}
                        aria-label="delete from history"
                        color="secondary">
                        <DeleteIcon fontSize="inherit"/>
                    </IconButton>;
                const hide = currentMode !== HistoryMode;
                if (cols[r].hide !== hide) {
                    cols[r].hide = hide;
                    changed = true;
                    cols = arrayMove(cols, r, cols.length);
                }
            }

            if (!changed) return current;

            // update renderers
            const t = cols.findIndex(c => c.variable === 'tag');
            if (t >= 0) {
                cols[t].renderCell = p => {
                    const v = typeof p.row === 'object' ? _get(p.row, 'data.bricata.tag') : p.row;
                    if (!v) return '';
                    return v.map((t, i) =>
                        <Tag
                            key={t + i}
                            tag={t}
                            onUntag={t => untag(p.row, t)}
                            untagLabel={'untag'}/>
                    );
                };
            }

            Object.keys(mappings).forEach(m => {
                const c = cols.findIndex(c => c.variable === m);
                if (c >= 0) {
                    cols[c].renderHeader = headerRenderers[m];
                    cols[c].renderCell = cellRenderers[m];
                }
            });
            return cols;
        });
    }, [
        groups,
        tableAPIRef,
        hideColumns.count,
        hideColumns.activity,
        hideColumns.db_timestamp,
        currentMode,
        onHistoryDelete,
        untag,
        mappings,
        getHeaderRenderers,
        getCellRenderers,
        backtesting,
    ]);

    useEffect(() => {
        if (!tableAPIRef || !tableAPIRef.current || !tableAPIRef.current.updateColumns) {
            return;
        }
        tableAPIRef.current.updateColumns(columns);
    }, [tableAPIRef, columns]);

    // profile update hook
    useEffect(() => {
        if (!init) return;
        // ignore first time profile is loaded
        if (!updatedRef.current) {
            updatedRef.current = true;
            return;
        }
        const currentState = getCurrentState();
        const profileData = getProfile(profile);
        const updated = {
            ...profileData,
            hideFilterbar,
            hideTimelinebar,
            state: {
                widths,
                hideColumns,
                ...currentState,
            }
        };

        // remove obsolete fields from profile
        const {
            data,
            filter: f,
            mode: m,
            tagsOp: to,
            read,
            sortCol,
            sortOrder,
            startTime,
            endTime,
            _dynamicColWidth,
            _quickFilter,
            _mode,
            _groups,
            showEnglish,
            ...rest
        } = updated;
        updateProfile(profile, rest);
    }, [
        init,
        getCurrentState,
        updateProfile,
        widths,
        getProfile,
        hideColumns,
        hideFilterbar,
        hideTimelinebar,
        profile,
    ]);

    return (
        <React.Fragment>
            <GroupsContext.Provider value={{
                maxGroup,
                groupKeys
            }}>
                {<main className={clsx(classes.content)}>
                    <Box style={{ position: 'absolute', right: 10, top: -35 }}>
                        {hideFilterbar && !props.hideFilterbar &&
                            <Button
                                color="primary"
                                onClick={toggleFilterbar}
                                aria-label="show filter panel"
                                startIcon={<ExpandMoreIcon />}>
                                Filters
                            </Button>}
                        {hideTimelinebar &&
                         !props.hideTimelinebar &&
                         !MC2CHECK &&
                            <Button
                                color="primary"
                                onClick={toggleTimelinebar}
                                aria-label="show timeline"
                                startIcon={<ExpandMoreIcon />}>
                                Timeline
                            </Button>}
                    </Box>
                    {/* FilterSearchCombo, backtesting, time selection panel */}
                    <FilterBar
                        gHideFilterbar={props.hideFilterbar}
                        { ...{
                            hideFilterbar,
                            hideTimelinebar,
                            handleSearchFilterChange,
                            showFilterTimeout,
                            filter,
                            search,
                            savedFilters,
                            setShowFilter,
                            showFilter,
                            fields,
                            perms,
                            handleSelectSavedFilter,
                            handleUpsertSavedFilter,
                            handleDeleteSavedFilter,
                            handleOnFilterExport,
                            handleOnFilterImport,
                            backtesting,
                            handleSetBacktesting,
                            handleBacktestingJobsUpdated,
                            selectedBTJobs,
                            handleSelectBTJobs,
                            english,
                            toggleFilterbar,
                            handleOnQuickFilter,
                            handleStartChange,
                            handleEndChange,
                            quick,
                            start: (range || []).length > 0 ? range[0] : undefined,
                            end: (range || []).length > 1 ? range[1] : undefined,
                            label: 'Alerts',
                        } } />
                    {!MC2CHECK && <Collapse
                        in={!hideTimelinebar && !props.hideTimelinebar}
                        style={{
                            marginLeft: -10,
                            display: props.hideTimelinebar ? 'hide' : '',
                        }}>
                        <Grid
                            container
                            spacing={1}
                            name="alert_charts"
                            direction="row"
                            alignItems="center"
                            wrap="nowrap"
                            justifyContent="space-between">
                            <Grid item>
                                <Grid container direction="row" justifyContent="flex-start"
                                    alignItems="center" wrap="nowrap">
                                    <BarChart
                                        dataservice={AlertsService}
                                        data={timeline}
                                        filter={groupFilter}
                                        height={TIMELINE_HEIGHT}
                                        mouseover={handleTimelineMouseover}
                                        onclick={handleTimelineClick}
                                        isBT={backtesting === BTMODE_BT}/>
                                    <DonutChart
                                        data={donutData}
                                        filter={donutFilter}
                                        size={TIMELINE_HEIGHT - 10}
                                        mouseover={handleDonutMouseover}
                                        onclick={handleDonutClick}/>
                                </Grid>
                            </Grid>
                            <Grid item>
                                <IconButton
                                    title="Hide Timeline"
                                    onClick={toggleTimelinebar}
                                    aria-label="Hide Timeline"
                                    size="large">
                                    <ExpandLessIcon fontSize="inherit"/>
                                </IconButton>
                            </Grid>
                        </Grid>
                    </Collapse>}
                    {showColumnsSel && <ColumnSelector
                        show={showColumnsSel}
                        columns={columns}
                        mappings={mappings}
                        sortCol={sort.split(' ').length && sort.split(' ')[0]}
                        sortOrder={sort.split(' ').length && sort.split(' ')[1]}
                        anchorEl={toolbarRef.current}
                        onClickAway={() => setShowColumnsSel(false)}
                        onOK={handleColumnsUpdated}/>}
                    {tagPerms.read && <TagList
                        show={showTags}
                        cmc={cmc}
                        placement='bottom-end'
                        onClickAway={() => setShowTags(false)}
                        anchorEl={toolbarRef.current}
                        onTagSelect={currentMode !== HistoryMode ? handleTagSelect : undefined}
                        onStartTagging={currentMode !== MapMode && perms.write
                            ? handleStartTagging : undefined}
                        onFinishTagging={currentMode !== MapMode && perms.write
                            ? handleFinishTagging : undefined}
                        onUntagFilter={currentMode !== MapMode && currentMode !== HistoryMode && perms.write && !props.miniToolbar
                            ? handleUntagFilter : undefined}
                        onTagFilter={currentMode !== MapMode && currentMode !== HistoryMode && perms.write && !props.miniToolbar
                            ? handleTagFilter : undefined}/>}
                    <MitreList
                        show={showMitre}
                        placement='bottom-end'
                        onClickAway={() => setShowMitre(false)}
                        anchorEl={toolbarRef.current}
                        onStartMitre={currentMode !== MapMode && perms.write
                            ? handleStartMitre : undefined}
                        onFinishMitre={currentMode !== MapMode && perms.write
                            ? handleFinishMitre : undefined}
                        onUnmitreFilter={currentMode !== MapMode && currentMode !== HistoryMode && perms.write && !props.miniToolbar
                            ? handleUnmitreFilter : undefined}
                        onMitreFilter={currentMode !== MapMode && currentMode !== HistoryMode && perms.write && !props.miniToolbar
                            ? handleMitreFilter : undefined}/>
                    <IncidentResponseList
                        show={showIncidents}
                        placement='bottom-end'
                        onClickAway={() => setShowIncidents(false)}
                        anchorEl={toolbarRef.current}
                        onSelect={currentMode !== HistoryMode ? handleTagSelect : undefined}
                        onStartSelection={currentMode !== MapMode ? handleStartIncidents : undefined}
                        onFinishSelection={currentMode !== MapMode ? handleFinishIncidents : undefined}/>
                </main>}
                <div ref={toolbarRef}/>
                <BubbleTabs
                    tabs={props.modes || modes}
                    selected={currentMode}
                    onClick={handleModeChange}
                    static={true}>
                    {currentMode === DetailMode && <div>
                        <AlertDetail
                            onPrev={prevAlert ? () => navigateToAlert(prevAlert) : undefined}
                            onNext={nextAlert ? () => navigateToAlert(nextAlert) : undefined}
                            onFilter={props.hideFilterbar ? undefined : handleAddFilter}
                            alert={currentAlert}
                            onTag={perms.write ? handleTagAlert : undefined}
                            onUntag={perms.write ? handleUntagAlert : undefined}
                            onSaveNote={perms.write ? handleSaveNote : undefined}
                            onReadAlert={doReadAlert}
                            onTune={gatorPerms.write ? handleTuneAlert : undefined}
                            onShare={handleShare}
                            header={props.headerSize || (height - tableHeight)}
                            cmc={cmc}
                            isBT={GetBTUUID(currentAlert, 'data.bt_uuid') !== undefined}/>
                    </div>}
                    {currentMode === MapMode && <div className={classes.mapview}>
                        <GeomapToolbar
                            tagFilters={tagFilters}
                            tagsOp={tagsOp}
                            pause={pause}
                            onTagAction={() => { setShowTags(true); setShowMitre(false) }}
                            onRemoveTag={handleRemoveTagFilter}
                            onTagsOpSelect={setTagsOp}
                            onPause={range[1] ? undefined : () => handleGeoPause(pause, refresh)}
                            cmc={cmc}
                            cmcs={cmcs}
                            onSetCmc={setCmc}/>
                        <GeoMap
                            data={geodata}
                            onFilterSrc={handleFilterSrc}
                            onFilterDest={handleFilterDest}
                            srcStr="Src IP"
                            destStr="Dest IP"
                            size={{ width: width - 100, height: tableHeight - 10 }}
                            isBT={backtesting === BTMODE_BT}
                            heartbeat={geoHeartbeat}/>
                    </div>}
                    <Grid style={{
                        margin: 8,
                        display: currentMode === MapMode || currentMode === DetailMode
                            ? 'none' : ''
                    }}
                    container
                    direction="row"
                    justifyContent="center" alignItems="center">
                        <Grid item xs={12}
                            className={clsx(classes.table,
                                { list: currentMode !== HistoryMode && groups.length < 1 })}
                            style={{ height: tableHeight }}>
                            {init && <DataGridPro
                                components={{
                                    Toolbar: TableToolbar,
                                    LoadingOverlay,
                                    NoRowsOverlay: MC2CHECK && !cmc
                                        ? NoCMCLoadingOverlay : NoRowsOverlay,
                                    Footer: currentMode !== HistoryMode ? TableFooter : undefined,
                                }}
                                apiRef={tableAPIRef}
                                loading={loading === true}
                                columns={columns}
                                rows={data}
                                columnBuffer={5}
                                rowBuffer={20}
                                rowHeight={38}
                                checkboxSelection={tagging}
                                sortingMode={props.sortingMode || 'server'}
                                sortingOrder={['desc', 'asc']}
                                onSortModelChange={handleOnSort}
                                sortModel={getSortModel()}
                                onError={e => console.error(e)}
                                disableMultipleColumnsSorting={true}
                                onSelectionModelChange={handleSelectionChange}
                                onRowClick={handleViewAlert}
                                onCellClick={handleOnCellClick}
                                total={total}
                                disableVirtualization={false}
                                disableColumnSelector={true}
                                disableColumnMenu={true}
                                onColumnOrderChange={handleColumnReorder}
                                onColumnResize={handleColumnResize}
                                onRowsScrollEnd={props.sortingMode === 'client' ? undefined : handleScrollEnd}
                                getRowId={getRowId}
                            />}
                        </Grid>
                    </Grid>
                </BubbleTabs>
                <Backdrop
                    className={classes.backdrop}
                    open={tagging}>
                </Backdrop>
                {/* ok/cancel dialog */}
                <ConfirmationDialog
                    onClose={(e, reason) => {
                        setConfirmDlg(false);
                    }}
                    onOK={confirmDlg ? confirmDlg.callback : () => {}}
                    open={Boolean(confirmDlg)}
                    message={confirmDlg && confirmDlg.msg}/>
                {/* pick a policy dialog */}
                {wantPolicy &&
                    <Dialog
                        disableEscapeKeyDown
                        maxWidth="xs"
                        aria-labelledby="select policy"
                        open={(wantPolicy.policies || []).length > 0}>
                        <DialogTitle> Select Data Tuning Policy </DialogTitle>
                        <DialogContent dividers>
                            Please select a Data Tuning policy <b/>
                            for the new tuning rule
                            <br/>
                            <PopupMenu
                                tooltip="select policy"
                                items={wantPolicy.policies}
                                selection={wantPolicy.policy}
                                onSelect={handleOnWantPolicySelect}
                                title={wantPolicy.policy}
                                placement={'bottom-start'}
                                name="policy selector"/>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                autoFocus
                                onClick={() => setWantPolicy()}
                                color="primary"
                                aria-label="cancel">
                                Cancel
                            </Button>
                            <Button
                                enabled={wantPolicy.policy}
                                onClick={() => handleOnWantPolicy()}
                                color="primary"
                                aria-label="add">
                                Add Rule
                            </Button>
                        </DialogActions>
                    </Dialog>
                }
            </GroupsContext.Provider>
        </React.Fragment>
    );
};

Alerts.propTypes = {
    hideTimelinebar: PropTypes.bool,
    hideFilterbar: PropTypes.bool,
    modes: PropTypes.array,
    service: PropTypes.object,
    headerSize: PropTypes.number,
    miniToolbar: PropTypes.bool,
    live: PropTypes.bool,
    sortingMode: PropTypes.string,
    profile: PropTypes.string,
};

export default Alerts;
